import { useState } from "preact/hooks";
import { useNavigate } from "../contexts/AppContext";
import { PublicLayout } from "./PublicLayout";

// Code-entry screen reached after signup or login of an unverified
// account. The server has already emailed a 6-digit code — the user
// types it back here. No links anywhere; deliberate per FT-AUTH.

export function Verify({
  email,
  onAuthed,
}: {
  email: string;
  onAuthed: (email: string) => void;
}) {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [resendStatus, setResendStatus] = useState<"" | "sending" | "sent">("");

  const submit = async (e: Event) => {
    e.preventDefault();
    if (code.trim().length !== 6) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/auth/verify-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: code.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Verification failed");
        return;
      }
      const data = await res.json();
      onAuthed(data.user.email);
      const params = new URLSearchParams(window.location.search);
      const next = params.get("next") || "/";
      navigate(next);
    } catch (err) {
      setError(String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    setResendStatus("sending");
    try {
      await fetch("/api/auth/resend-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setResendStatus("sent");
    } catch {
      setResendStatus("");
    }
  };

  return (
    <PublicLayout>
      <div class="container py-5">
        <div class="row justify-content-center">
          <div class="col-md-6 col-lg-5">
            <div class="card shadow-sm">
              <div class="card-body p-4">
                <h1 class="h3 mb-3 text-center">Verify your email</h1>
                <p class="small text-muted text-center mb-4">
                  We sent a 6-digit code to <strong>{email}</strong>. Enter it
                  below to finish signing in.
                </p>
                <form onSubmit={submit}>
                  <div class="mb-3">
                    <label class="form-label">Code</label>
                    <input
                      type="text"
                      class="form-control form-control-lg text-center font-monospace"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      pattern="\d{6}"
                      value={code}
                      onInput={(e) =>
                        setCode(
                          (e.target as HTMLInputElement).value.replace(
                            /\D/g,
                            "",
                          ),
                        )
                      }
                      autoFocus
                      required
                    />
                  </div>
                  {error && <div class="alert alert-danger py-2">{error}</div>}
                  <button
                    type="submit"
                    class="btn btn-primary w-100"
                    disabled={submitting || code.length !== 6}
                  >
                    <i class="bi bi-check-lg"></i>{" "}
                    {submitting ? "..." : "Verify"}
                  </button>
                </form>
                <div class="text-center mt-3 small">
                  {resendStatus === "sent" ? (
                    <span class="text-muted">
                      New code sent. Check your inbox.
                    </span>
                  ) : (
                    <>
                      Didn't get the email?{" "}
                      <button
                        type="button"
                        class="btn btn-link btn-sm p-0 align-baseline"
                        onClick={resend}
                        disabled={resendStatus === "sending"}
                      >
                        Resend
                      </button>
                    </>
                  )}
                </div>
                <div class="text-center mt-2 small">
                  <a
                    href="/login"
                    onClick={(e: Event) => {
                      e.preventDefault();
                      navigate("/login");
                    }}
                  >
                    Use a different email
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
